import { useState, useEffect, useRef, useMemo } from 'react';
import Stack from 'react-bootstrap/Stack';
import ListGroup from 'react-bootstrap/ListGroup';
import Spinner from 'react-bootstrap/Spinner';

import WordSearchInput from './WordSearchInput';
import CandidateListItem from './CandidateListItem';

import { searchWords } from '../services/wordsService';
import { addWordToUser } from '../services/usersService';
import { initOfflineSearch, searchOffline } from '../services/offlineSearchService';
import { normalizeText } from '../utils/textNormalize';

const REMOTE_TRIGGER_THRESHOLD = 7;
const MIN_REMOTE_QUERY_LENGTH = 3;
const DISPLAY_RESULT_LIMIT = 10;
const OFFLINE_FETCH_LIMIT = 25;

const buildEntryKey = (value, lang = 'en') => {
  if (!value) return null;
  const normalizedValue = normalizeText(value);
  if (!normalizedValue) return null;
  return `${lang.toLowerCase()}::${normalizedValue}`;
};

const getCandidateKey = (candidate) =>
  buildEntryKey(
    candidate?.normalized || candidate?.word || candidate?.slug,
    candidate?.lang || 'en',
  );

const WordSearch = ({ onWordAdded, showAlert, existingEntries = [], userId, getAccessToken }) => {
  const [word, setWord] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [offlineStatus, setOfflineStatus] = useState({ ready: false, error: false });
  const [offlineLoading, setOfflineLoading] = useState(false);
  const [remoteLoading, setRemoteLoading] = useState(false);
  const searchRequestRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    initOfflineSearch()
      .then(() => {
        if (isMounted) {
          setOfflineStatus({ ready: true, error: false });
        }
      })
      .catch(() => {
        if (isMounted) {
          setOfflineStatus({ ready: false, error: true });
        }
      });
    return () => {
      isMounted = false;
    };
  }, []);

  const existingWordSet = useMemo(() => {
    const set = new Set();
    (existingEntries || []).forEach((entry) => {
      const key = buildEntryKey(entry.normalized || entry.slug || entry.word, entry.lang || 'en');
      if (key) set.add(key);
    });
    return set;
  }, [existingEntries]);

  const annotateExisting = (items) =>
    (items || []).map((candidate) => {
      const key = getCandidateKey(candidate);
      const exists = candidate.exists || (key ? existingWordSet.has(key) : false);
      return { ...candidate, exists };
    });

  useEffect(() => {
    setCandidates((prev) => annotateExisting(prev));
  }, [existingWordSet]);

  const TYPE_PRIORITY = {
    word: 0,
    phrase: 1,
    idiom: 2,
  };

  // Rank offline/API results together so exact/prefix matches surface even when offline returns many fuzzy hits.
  const mergeCandidates = (primary, secondary, query) => {
    const normalizedQuery = normalizeText(query || '');
    const slugQuery = normalizedQuery.replace(/\s+/g, '-');

    const matchPriority = (candidate) => {
      if (!candidate) return Number.POSITIVE_INFINITY;
      const normalizedWord = candidate.normalized || normalizeText(candidate.word || '');
      const slug = candidate.slug ? candidate.slug.toLowerCase() : '';

      const normalizedMatches =
        normalizedQuery &&
        (normalizedWord === normalizedQuery ||
          normalizedWord.startsWith(normalizedQuery) ||
          slug === slugQuery ||
          slug.startsWith(slugQuery));

      if (normalizedMatches) return 0;

      if (candidate.strategy === 'exact') return 0;
      if (candidate.strategy === 'prefix') return 1;
      if (candidate.strategy === 'fuzzy') return 3;
      if (candidate.strategy === 'phonetic') return 4;
      return 5;
    };

    const compareCandidates = (a, b) => {
      const priorityDiff = matchPriority(a) - matchPriority(b);
      if (priorityDiff !== 0) return priorityDiff;

      const scoreA = a.sortScore ?? a.score ?? 1;
      const scoreB = b.sortScore ?? b.score ?? 1;
      const scoreDiff = scoreA - scoreB;
      if (scoreDiff !== 0) return scoreDiff;

      const freqDiff =
        (a.frequencyRank ?? Number.POSITIVE_INFINITY) -
        (b.frequencyRank ?? Number.POSITIVE_INFINITY);
      if (freqDiff !== 0) return freqDiff;

      const typeDiff = (TYPE_PRIORITY[a.kind] ?? 3) - (TYPE_PRIORITY[b.kind] ?? 3);
      if (typeDiff !== 0) return typeDiff;

      return (a.word || '').localeCompare(b.word || '', undefined, { sensitivity: 'base' });
    };

    const merged = [];
    const seen = new Set();
    const pushCandidate = (candidate) => {
      if (!candidate || !candidate.word) return;
      const key =
        getCandidateKey(candidate) || `${candidate.word.toLowerCase()}-${candidate.lang || 'en'}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(candidate);
    };
    primary.forEach(pushCandidate);
    secondary.forEach(pushCandidate);
    merged.sort(compareCandidates);
    return merged.slice(0, DISPLAY_RESULT_LIMIT);
  };

  const normalizeApiCandidates = (items) =>
    (items || []).map((item) => ({
      ...item,
      normalized: item.normalized || normalizeText(item.word || item.slug || ''),
      // Datamuse returns higher scores for better matches; negate to sort ascending with offline scores.
      sortScore: typeof item.score === 'number' ? -item.score : null,
      source: item.source || 'api',
      kind:
        item.kind ||
        (item.tags?.includes('idiom')
          ? 'idiom'
          : item.tags?.includes('phrase')
            ? 'phrase'
            : 'word'),
    }));

  const triggerRemoteSearch = (query, requestId) => {
    if (!getAccessToken) {
      setRemoteLoading(false);
      return;
    }
    setRemoteLoading(true);
    searchWords({ search: query }, getAccessToken)
      .then((result) => {
        if (requestId !== searchRequestRef.current) return;
        const remoteCandidates = annotateExisting(normalizeApiCandidates(result.data.words || []));
        setCandidates((current) => mergeCandidates(current, remoteCandidates, query));
      })
      .catch(() => {
        if (requestId === searchRequestRef.current) {
          showAlert('Failed to search words. Please try again.', 'danger');
        }
      })
      .finally(() => {
        if (requestId === searchRequestRef.current) {
          setRemoteLoading(false);
        }
      });
  };

  const handleChange = (event) => {
    const value = event.target.value;
    setWord(value);
    const query = value.trim();
    searchRequestRef.current += 1;
    const requestId = searchRequestRef.current;
    setRemoteLoading(false);

    if (!query) {
      setCandidates([]);
      setOfflineLoading(false);
      setRemoteLoading(false);
      return;
    }

    setOfflineLoading(true);
    searchOffline(query, { limit: OFFLINE_FETCH_LIMIT })
      .then((offlineResults) => {
        if (requestId !== searchRequestRef.current) return;
        const normalizedOffline = offlineResults || [];
        const annotatedOffline = annotateExisting(normalizedOffline).slice(0, DISPLAY_RESULT_LIMIT);
        setCandidates(annotatedOffline);
        const normalizedQuery = normalizeText(query);
        const slugQuery = normalizedQuery.replace(/\s+/g, '-');
        const hasStrongOfflineMatch = normalizedOffline.some((candidate) => {
          const normalizedWord =
            candidate?.normalized || normalizeText(candidate?.word || candidate?.slug || '');
          const slug = candidate?.slug ? candidate.slug.toLowerCase() : '';
          const strategyMatch = candidate?.strategy === 'exact' || candidate?.strategy === 'prefix';
          const normalizedMatch =
            normalizedWord === normalizedQuery ||
            normalizedWord.startsWith(normalizedQuery) ||
            slug === slugQuery ||
            slug.startsWith(slugQuery);
          return strategyMatch || normalizedMatch;
        });
        const shouldFallbackToApi =
          query.length >= MIN_REMOTE_QUERY_LENGTH &&
          (normalizedOffline.length < REMOTE_TRIGGER_THRESHOLD || !hasStrongOfflineMatch);
        if (shouldFallbackToApi) {
          triggerRemoteSearch(query, requestId);
        } else {
          setRemoteLoading(false);
        }
      })
      .catch(() => {
        if (requestId === searchRequestRef.current) {
          setOfflineStatus((prev) => ({ ...prev, error: true }));
          if (query.length >= MIN_REMOTE_QUERY_LENGTH) {
            triggerRemoteSearch(query, requestId);
          }
        }
      })
      .finally(() => {
        if (requestId === searchRequestRef.current) {
          setOfflineLoading(false);
        }
      });
  };

  const showSpinner = Boolean(word) && candidates.length === 0 && (offlineLoading || remoteLoading);

  const handleCandidateSelect = (candidate) => {
    const metadata = {
      platform: 'web',
      source: 'searchbox',
      type: 'search',
      searchApi: candidate.source === 'offline' ? 'offline-wordbank' : 'datamuse',
    };
    if (candidate.strategy) metadata.searchStrategy = candidate.strategy;
    if (typeof candidate.position === 'number') metadata.suggestionIndex = candidate.position;
    if (word) metadata.searchQuery = word;
    if (candidate.kind) metadata.wordKind = candidate.kind;
    if (candidate.bankId) metadata.bankId = candidate.bankId;

    if (!getAccessToken) {
      showAlert('User is not ready yet. Please try again.', 'danger');
      return;
    }

    addWordToUser(
      {
        word: candidate.word,
        metadata,
      },
      getAccessToken,
    )
      .then(() => {
        setWord('');
        setCandidates([]);
        onWordAdded(candidate.word);
      })
      .catch(() => {
        showAlert('Failed to add word. Please try again.', 'danger');
      });
  };

  const handleCustomWordAdd = (event) => {
    event.preventDefault();
    if (!word || !getAccessToken) {
      showAlert('User is not ready yet. Please try again.', 'danger');
      return;
    }

    addWordToUser(
      {
        word: word,
        metadata: {
          platform: 'web',
          source: 'searchbox',
          type: 'custom',
          searchApi: null,
        },
      },
      getAccessToken,
    )
      .then(() => {
        setWord('');
        setCandidates([]);
        onWordAdded(word);
      })
      .catch(() => {
        showAlert('Failed to add word. Please try again.', 'danger');
      });
  };

  return (
    <div className='mb-3'>
      <WordSearchInput value={word} onChange={handleChange} />
      {showSpinner && (
        <div className='d-flex justify-content-center mt-3 mb-3'>
          <Spinner animation='border' size='sm' />
        </div>
      )}
      {word && candidates.length > 0 && (
        <div>
          <Stack direction='horizontal' gap={3}>
            <p className='candidateList'>Tap a word to add:</p>
            <p className='candidateList ms-auto'>
              <a href='#' onClick={handleCustomWordAdd} style={{ color: '#212529' }}>
                or tap to add <strong>{word}</strong>
              </a>
            </p>
          </Stack>
          <ListGroup>
            {candidates.map((candidate, index) => (
              <CandidateListItem
                key={candidate.id || candidate.word}
                candidate={{ ...candidate, position: index }}
                onSelect={handleCandidateSelect}
              />
            ))}
          </ListGroup>
        </div>
      )}
      {word && candidates.length === 0 && !showSpinner && (
        <div>
          <p className='candidateList'>
            <a href='#' onClick={handleCustomWordAdd} style={{ color: '#212529' }}>
              Tap to add <strong>{word}</strong>
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

export default WordSearch;
